import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { Star, Check, XCircle, Loader, MessageSquareQuote } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const authed = () => {
  const t = localStorage.getItem("admin_session_token");
  return { withCredentials: true, headers: t ? { Authorization: `Bearer ${t}` } : {} };
};

const TABS = [
  { key: "PENDING", label: "Pending" },
  { key: "APPROVED", label: "Approved" },
  { key: "REJECTED", label: "Rejected" },
];

function Stars({ value }) {
  return (
    <span className="star-row small">
      {[1, 2, 3, 4, 5].map((n) => (
        <Star key={n} size={12} fill={n <= value ? "currentColor" : "none"} />
      ))}
    </span>
  );
}

export default function AdminReviews() {
  const [tab, setTab] = useState("PENDING");
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/admin/reviews`, { ...authed(), params: { status: tab } });
      setReviews(r.data.reviews || []);
    } catch (err) {
      console.error("Failed to load reviews:", err);
    } finally {
      setLoading(false);
    }
  }, [tab]);

  useEffect(() => { load(); }, [load]);

  async function act(id, verb) {
    try {
      await axios.patch(`${API}/admin/reviews/${id}/${verb}`, null, authed());
      await load();
    } catch (e) {
      alert(e?.response?.data?.detail || "Could not update review.");
    }
  }

  return (
    <div data-testid="admin-reviews">
      <p className="eyebrow"><MessageSquareQuote size={13} /> Customer reviews</p>
      <h1>Review moderation</h1>
      <p className="muted">Approve honest reviews to publish them on the Zorba profile page. Reject spam or anything that misrepresents the spa.</p>

      <div className="admin-tabs">
        {TABS.map((t) => (
          <button
            key={t.key}
            type="button"
            onClick={() => setTab(t.key)}
            className={`admin-tab${tab === t.key ? " active" : ""}`}
            data-testid={`admin-reviews-tab-${t.key.toLowerCase()}`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {loading ? (
        <p className="muted"><Loader size={14} className="spin" /> Loading…</p>
      ) : reviews.length === 0 ? (
        <p className="muted">No {tab.toLowerCase()} reviews.</p>
      ) : (
        <div className="admin-review-list">
          {reviews.map((r) => (
            <article key={r.id} className="admin-review-card" data-testid={`admin-review-${r.id}`}>
              <header>
                <div>
                  <strong>{r.name}</strong> · <span className="muted-small">{r.service}</span>
                </div>
                <Stars value={r.rating} />
              </header>
              <p>{r.text}</p>
              {r.photos?.length > 0 && (
                <div className="admin-review-photos">
                  {r.photos.map((p, i) => (
                    <img key={p} src={`${API}/files/${p}`} alt={`photo-${i + 1}`} loading="lazy" />
                  ))}
                </div>
              )}
              <footer>
                <span className="muted-small">Submitted {r.created_at?.slice(0, 10)}</span>
                <div className="actions-row" style={{ gap: 8, margin: 0 }}>
                  {r.status !== "APPROVED" && (
                    <button
                      type="button"
                      className="btn btn-primary"
                      onClick={() => act(r.id, "approve")}
                      data-testid={`admin-review-approve-${r.id}`}
                    >
                      <Check size={13} /> Approve
                    </button>
                  )}
                  {r.status !== "REJECTED" && (
                    <button
                      type="button"
                      className="btn btn-secondary"
                      onClick={() => act(r.id, "reject")}
                      data-testid={`admin-review-reject-${r.id}`}
                    >
                      <XCircle size={13} /> Reject
                    </button>
                  )}
                </div>
              </footer>
            </article>
          ))}
        </div>
      )}
    </div>
  );
}
